/* Reviews — numbered pagination with an autoplay progress ring.
 *
 * The ring around the active number fills over the configured interval
 * (data-revs-autoplay, ms), then the next review shows, wrapping at the
 * end. The left headline swaps with the slide. Clicking a number jumps
 * and restarts the clock.
 *
 * ONE CLOCK, TWO OWNERS. The advance is a setTimeout and nothing else —
 * rAF starves in background and headless tabs, and the slide must advance
 * regardless. The ring's *appearance* is a pure CSS animation whose
 * duration is this same interval (--revs-dur), so the browser interpolates
 * every frame itself. Nothing here runs per frame.
 *
 * That is the fix for the old build: it drove the ring from rAF, writing
 * stroke-dashoffset inline ~60×/second. Whenever the main thread was busy
 * the ring stuttered, and whenever rAF was throttled the ring froze while
 * the setTimeout advanced the slide underneath it — two clocks disagreeing.
 * Pausing is now a single attribute: CSS freezes the animation exactly
 * where it stands, and the timer is re-armed with the remaining time.
 *
 * Autoplay behaves itself: it pauses while the pointer is over the section,
 * while focus is inside it, and while the tab is hidden — each of those is
 * an independent hold, so leaving one does not resume while another stands.
 * It does not run at all under prefers-reduced-motion; the circles stay as
 * plain, clickable pagination. One review (or the pending policy slide)
 * means no pager, no timer, nothing to do. WCAG 2.2.2.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.revsReady) return;
    root.dataset.revsReady = '1';

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-rev-slide]'));
    var pages = Array.prototype.slice.call(root.querySelectorAll('[data-rev-goto]'));
    var intros = Array.prototype.slice.call(root.querySelectorAll('[data-rev-intro]'));
    if (slides.length < 2 || !pages.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var interval = reduced ? 0 : (parseInt(root.dataset.revsAutoplay, 10) || 0);
    var idx = 0;
    var timer = null;
    var startedAt = performance.now();   // never 0: a hold before the first
                                         // arm would bank a nonsense elapsed
    var elapsedBeforePause = 0;
    var holds = {};        // reason -> true. Any hold pauses; all must clear.
    var holdCount = 0;

    // The ring only animates when this attribute is present, and it reads its
    // duration from the same number the timer uses — they cannot drift.
    if (interval) {
      root.style.setProperty('--revs-dur', interval + 'ms');
      root.dataset.revsTicking = '1';
    }

    function render() {
      slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === idx);
        s.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
      });
      intros.forEach(function (n, i) {
        n.classList.toggle('is-active', i === idx);
        if (i === idx) n.removeAttribute('aria-hidden');
        else n.setAttribute('aria-hidden', 'true');
      });
      pages.forEach(function (p, i) {
        p.classList.toggle('is-active', i === idx);
        if (i === idx) p.setAttribute('aria-current', 'true');
        else p.removeAttribute('aria-current');
      });
    }

    /* Re-run the ring animation from zero.
     *
     * Moving .is-active to a different number restarts it for free — that
     * element had no animation a moment ago. The case that needs this is
     * clicking the number that is ALREADY active: the class does not change,
     * so without a restart the ring would carry on from mid-fill while the
     * timer started over. One forced reflow per slide change, never per frame.
     * offsetWidth is read on root (an HTML element) because SVG elements do
     * not carry it. */
    function restartRing() {
      var arc = pages[idx] && pages[idx].querySelector('.revs__ring-arc');
      if (!arc) return;
      arc.style.animation = 'none';
      void root.offsetWidth;
      arc.style.animation = '';
    }

    function advance() {
      idx = (idx + 1) % slides.length;
      render();
      restart();
    }

    function arm(ms) {
      clearTimeout(timer);
      timer = setTimeout(advance, Math.max(0, ms));
      startedAt = performance.now();
    }

    function restart() {
      if (!interval) return;
      clearTimeout(timer);
      timer = null;
      elapsedBeforePause = 0;
      restartRing();
      if (holdCount) return;          // release() will arm it
      arm(interval);
    }

    function hold(reason) {
      if (!interval || holds[reason]) return;
      holds[reason] = true;
      if (++holdCount > 1) return;
      root.dataset.revsPaused = '1';  // CSS freezes the ring here
      elapsedBeforePause += performance.now() - startedAt;
      clearTimeout(timer);
      timer = null;
    }

    function release(reason) {
      if (!interval || !holds[reason]) return;
      delete holds[reason];
      if (--holdCount > 0) return;
      delete root.dataset.revsPaused;
      arm(interval - elapsedBeforePause);
    }

    pages.forEach(function (p) {
      p.addEventListener('click', function () {
        idx = parseInt(p.dataset.revGoto, 10) || 0;
        render();
        restart();
      });
    });

    root.addEventListener('pointerenter', function () { hold('pointer'); });
    root.addEventListener('pointerleave', function () { release('pointer'); });
    root.addEventListener('focusin', function () { hold('focus'); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) release('focus');
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) hold('hidden'); else release('hidden');
    });

    render();
    restart();
  }

  function boot() {
    document.querySelectorAll('[data-revs]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
